/**
 * Tour anchor resolution (#124).
 *
 * The guided tour spotlights REAL components, which tag their target element
 * with a `data-tour="<name>"` attribute (attribute-only — see the components
 * listed in each tour's steps). This hook resolves the active step's anchor
 * name to a live element and tracks its viewport rect, so the Spotlight hole +
 * StepCard can sit over it.
 *
 * RESILIENCE (the whole point of this hook). Page layouts change — a panel
 * collapses, a responsive breakpoint swaps which node renders, a tab hides its
 * body, a feature is off for this org, the target only mounts after the user
 * opens a dialog. A naive "querySelector once" tour highlights the wrong thing
 * or freezes when any of that happens. So we:
 *   1. Try `document.querySelector` immediately.
 *   2. If not found, watch the DOM with a MutationObserver AND poll on a short
 *      interval (covers nodes that mount without a subtree mutation we catch).
 *   3. KEEP watching after we resolve. If the resolved element later leaves the
 *      DOM (`!isConnected`) — collapsed panel, route swap, responsive remount —
 *      we drop it and re-enter hunting so the spotlight re-acquires the *new*
 *      element instead of tracking a stale, detached rect.
 *   4. Track the rect on resize (window + visualViewport), scroll (capture
 *      phase, so nested scroll containers like a sheet body count), and a
 *      rAF loop while the layout settles — so the highlight follows the element
 *      as things shift, not just on discrete events.
 *   5. Give up after `timeoutMs` if never found, surfacing `timedOut = true`.
 *      The hook NEVER throws and never blocks: a not-found anchor leaves
 *      `rect = null` and the caller decides (center as guidance for an optional
 *      anchor, or auto-skip the step for a required one).
 *
 * Returns `{ rect, resolved, timedOut }`. `resolved` flips true once the element
 * is found at least once (and is currently connected). `timedOut` flips true if
 * the wait elapsed without ever finding it — the caller's cue to skip.
 */

import { useEffect, useRef, useState } from "react";

export interface TourAnchorRect {
  /** Current viewport-space rect of the anchor, or null while unresolved. */
  rect: DOMRect | null;
  /** True while a live, connected anchor element is currently being tracked. */
  resolved: boolean;
  /**
   * True if `timeoutMs` elapsed without the anchor ever being found. Stays false
   * for an anchor that resolves (even if it later detaches — that re-enters
   * hunting rather than reporting a timeout). The caller uses this to auto-skip
   * a required step whose target simply isn't on this page/layout.
   */
  timedOut: boolean;
}

/** Find the element tagged with the given data-tour name (first match). */
function queryAnchor(name: string): HTMLElement | null {
  if (typeof document === "undefined") return null;
  return document.querySelector<HTMLElement>(`[data-tour="${CSS.escape(name)}"]`);
}

/**
 * Resolve + track the rect for a `data-tour` anchor name. Pass `null` to
 * disable (e.g. no active tour). `timeoutMs` bounds how long we wait for a
 * not-yet-mounted target before reporting `timedOut` (the caller's skip cue).
 */
export function useTourAnchor(
  name: string | null,
  timeoutMs = 8000,
): TourAnchorRect {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [resolved, setResolved] = useState(false);
  const [timedOut, setTimedOut] = useState(false);

  // The element we're currently tracking, in a ref so the listeners/poll always
  // read the latest without re-subscribing. Cleared when it detaches.
  const elRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    setRect(null);
    setResolved(false);
    setTimedOut(false);
    elRef.current = null;
    if (!name) return;

    let cancelled = false;
    let foundOnce = false;
    let rafId = 0;

    // Re-measure the tracked element. If it has been detached from the DOM
    // (panel collapsed, responsive remount, route swapped), forget it and fall
    // back to hunting so we re-acquire whatever now carries the anchor — instead
    // of pinning the spotlight to a stale rect off-screen.
    const measure = () => {
      if (cancelled) return;
      const el = elRef.current;
      if (el && !el.isConnected) {
        elRef.current = null;
        setResolved(false);
        setRect(null);
        // Try to immediately re-acquire a replacement (e.g. the element
        // re-rendered under a different parent at the same breakpoint).
        const replacement = queryAnchor(name);
        if (replacement) {
          onFound(replacement);
        }
        return;
      }
      if (el) setRect(el.getBoundingClientRect());
    };

    const onFound = (found: HTMLElement) => {
      if (cancelled) return;
      elRef.current = found;
      foundOnce = true;
      setResolved(true);
      // A real target was found — it never "timed out", even if it had
      // previously elapsed and then mounted late.
      setTimedOut(false);
      setRect(found.getBoundingClientRect());
    };

    // 1. Immediate attempt.
    const immediate = queryAnchor(name);
    if (immediate) onFound(immediate);

    // 2. Watch the DOM for the target to (re)appear. Kept alive for the whole
    //    step — not just until first resolve — so a target that unmounts then
    //    remounts (collapsed→expanded panel, breakpoint flip) is re-acquired.
    const observer = new MutationObserver(() => {
      if (cancelled) return;
      const current = elRef.current;
      // Still tracking a live element → just keep its rect fresh.
      if (current && current.isConnected) {
        measure();
        return;
      }
      const found = queryAnchor(name);
      if (found) onFound(found);
      else if (current) measure(); // tracked element detached — let measure reset
    });
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["data-tour"],
    });

    // 3. Poll as a backstop (some mounts/relayouts don't surface a mutation we
    //    catch, and the rect can drift as async content loads in).
    const poll = window.setInterval(() => {
      const current = elRef.current;
      if (current && current.isConnected) {
        measure();
        return;
      }
      const found = queryAnchor(name);
      if (found) onFound(found);
    }, 200);

    // 4. Timeout — if we never found it, surface that so the caller can skip a
    //    required step (or center as guidance for an optional one). We keep the
    //    observer + poll running afterwards: a late mount still resolves and
    //    clears the timed-out flag, so a slow page never permanently breaks.
    const timeout = window.setTimeout(() => {
      if (cancelled) return;
      if (!foundOnce) setTimedOut(true);
    }, timeoutMs);

    // 5. Keep the rect glued to the element as the layout shifts.
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);
    // visualViewport fires on mobile URL-bar / zoom / keyboard changes that
    // don't raise a window resize.
    const vv = window.visualViewport;
    vv?.addEventListener("resize", measure);
    vv?.addEventListener("scroll", measure);

    // A short rAF settle loop right after (re)mount: catches the rect moving as
    // transitions/animations run (a sheet sliding in, content reflowing) before
    // the discrete events would. Self-cancels after a few hundred ms.
    const settleStart = performance.now();
    const settle = () => {
      if (cancelled) return;
      measure();
      if (performance.now() - settleStart < 600) {
        rafId = window.requestAnimationFrame(settle);
      }
    };
    rafId = window.requestAnimationFrame(settle);

    return () => {
      cancelled = true;
      elRef.current = null;
      observer.disconnect();
      window.clearInterval(poll);
      window.clearTimeout(timeout);
      window.cancelAnimationFrame(rafId);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      vv?.removeEventListener("resize", measure);
      vv?.removeEventListener("scroll", measure);
    };
  }, [name, timeoutMs]);

  return { rect, resolved, timedOut };
}
